import { Box, CircularProgress, Typography, useTheme } from '@mui/material';
import {
  BarController,
  BarElement,
  CategoryScale,
  Chart as ChartJS,
  ChartData,
  ChartOptions,
  LinearScale,
  PointElement,
  Title,
  Tooltip,
} from 'chart.js';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import { useMemo } from 'react';
import { Chart } from 'react-chartjs-2';

import { AnalyticsService } from '@/services';
import {
  selectReportDateRange,
  useAppSelector,
  useDownloadListenerDistributionReportCsvMutation,
  useFetchListenerDistributionReportQuery,
} from '@/store';
import { CsvVariants } from '@/types';

import { styles } from '../../styles';
import { EmptyChartView, EmptyViewImageKeys } from '../EmptyChartView';
import { TileHeader } from '../TileHeader';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarController,
  BarElement,
  Title,
  Tooltip,
  PointElement,
  ChartDataLabels,
);

export type BarChartData = ChartData<'bar', number[]>;

export const ListenerDistribution = () => {
  const { palette } = useTheme();
  const dateRange = useAppSelector(selectReportDateRange);
  const { data, isFetching } = useFetchListenerDistributionReportQuery(
    { dateRange },
    {
      skip: !dateRange.fromDate || !dateRange.toDate,
    },
  );
  const [downloadCsv, { isLoading: isDownloadingCsv }] =
    useDownloadListenerDistributionReportCsvMutation();

  const isEmpty = useMemo(() => {
    return data?.every(({ users }) => users === 0);
  }, [data]);

  const chartData: BarChartData = useMemo(() => {
    if (!data || isEmpty) return { labels: [], datasets: [] };

    return {
      labels: data.map(({ episodeQuantityBucket }) =>
        episodeQuantityBucket.toString(),
      ),
      datasets: [
        {
          label: 'Members',
          type: 'bar' as const,
          data: data.map(({ users }) => users),
          borderRadius: 4,
          backgroundColor: palette.charts?.reports?.distribution?.main,
          hoverBackgroundColor: palette.charts?.reports?.distribution?.hover,
          datalabels: {
            anchor: 'end',
            align: 'end',
            formatter: (_, context) => {
              return `${Math.round(data[context.dataIndex].percentageOfTotalUsers)}%`;
            },
          },
        },
      ],
    };
  }, [data, isEmpty, palette]);

  return (
    <Box sx={[styles.tile, styles.tileWithMinHeight, styles.column]}>
      <TileHeader
        title="Listener Distribution"
        description="Breakdown of your team by how many episodes they listened to in the selected time period. Compliance earned is not included in this metric."
        buttonProps={{
          onClick: () => {
            downloadCsv({ dateRange });
            AnalyticsService.downloadReportingCsv({
              reportType: CsvVariants.LISTENER_DISTRIBUTION,
            });
          },
          isLoading: isDownloadingCsv,
        }}
      />
      {isFetching && (
        <Box
          sx={{
            width: '100%',
            height: '100%',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
          }}>
          <CircularProgress />
        </Box>
      )}
      {!isFetching && isEmpty && (
        <EmptyChartView
          title={EMPTY_CHART_VIEW_TITLE}
          subtitle={EMPTY_CHART_VIEW_SUBTITLE}
          imageKey={EmptyViewImageKeys.books}
        />
      )}
      {!isFetching && !isEmpty && (
        <>
          <Box>
            <Box sx={{ height: 280, mb: 2 }}>
              <Chart
                type={'bar'}
                options={chartConfig}
                data={chartData}
                width={'100%'}
                height={'100%'}
              />
            </Box>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}>
              <Box
                sx={{
                  height: 12,
                  width: 12,
                  backgroundColor: 'charts.reports.distribution.main',
                  borderRadius: 0.5,
                  mr: 1,
                }}
              />
              <Typography variant="body3">Active Users</Typography>
            </Box>
          </Box>
        </>
      )}
    </Box>
  );
};

const chartConfig: ChartOptions<'bar'> = {
  indexAxis: 'y',
  maintainAspectRatio: false,
  plugins: {
    legend: {
      display: false,
    },
    tooltip: {
      displayColors: false,
      mode: 'nearest' as const,
      intersect: true,
      callbacks: {
        title: () => '',
        label: context => context.parsed.x?.toString(),
      },
    },
  },
  responsive: true,
  scales: {
    x: {
      display: true,
      grid: {
        display: true,
      },
      grace: '20%',
      ticks: {
        maxTicksLimit: 6,
      },
    },
    y: {
      display: true,
      grid: {
        display: false,
      },
    },
  },
};

const EMPTY_CHART_VIEW_TITLE = 'Not enough listening data yet';
const EMPTY_CHART_VIEW_SUBTITLE =
  "Once your team starts engaging with content, you'll see how listening is distributed across users.";
